import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MAX_EQUIPMENT_TAG_DEPTH,
  validateEquipmentTagParent,
} from "@production-hub/shared";
import { IconPicker } from "@/components/equipment/IconPicker";
import { TagTreeSelector } from "@/components/equipment/EquipmentTagPicker";
import {
  useCreateEquipmentTag,
  useEquipmentTags,
  useEquipmentTagsTree,
  useUpdateEquipmentTag,
  type EquipmentTag,
} from "@/hooks/useEquipmentTags";

interface EquipmentTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tag to edit; omit to create a new tag */
  tag?: EquipmentTag | null;
  /** Preselected parent when creating a tag */
  defaultParentId?: string | null;
}

/** Create or edit an equipment tag: name, parent and icon. */
export function EquipmentTagDialog({
  open,
  onOpenChange,
  tag,
  defaultParentId = null,
}: EquipmentTagDialogProps) {
  const { data: tags = [] } = useEquipmentTags();
  const { data: tagTree = [] } = useEquipmentTagsTree();
  const createTag = useCreateEquipmentTag();
  const updateTag = useUpdateEquipmentTag();

  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string | null>(null);
  const [icon, setIcon] = useState("");

  // Reset the form whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setName(tag?.name ?? "");
    setParentId(tag ? tag.parent_id : defaultParentId);
    setIcon(tag?.icon ?? "");
  }, [open, tag, defaultParentId]);

  // Only offer parents that keep the tag (and its sub-tags) within
  // MAX_EQUIPMENT_TAG_DEPTH levels and do not create a cycle. The current
  // parent stays selectable so older, deeper tags can still be renamed.
  const excludedParentIds = useMemo(() => {
    const links = tags.map((t) => ({ id: t.id, parentID: t.parent_id }));
    return tags
      .filter(
        (candidate) =>
          candidate.id !== tag?.parent_id &&
          validateEquipmentTagParent(links, tag?.id ?? null, candidate.id) !==
            null,
      )
      .map((candidate) => candidate.id);
  }, [tag, tags]);

  const isPending = createTag.isPending || updateTag.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const close = () => onOpenChange(false);
    if (tag) {
      updateTag.mutate(
        {
          id: tag.id,
          name: trimmed,
          // Only send the parent when it changed; moving is validated
          parent_id: parentId !== tag.parent_id ? parentId : undefined,
          icon: icon || null,
        },
        { onSuccess: close },
      );
    } else {
      createTag.mutate(
        { name: trimmed, parent_id: parentId, icon: icon || null },
        { onSuccess: close },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{tag ? "Edit Tag" : "Create Tag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="equipment-tag-name">Name</Label>
              <Input
                id="equipment-tag-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Kameras"
                autoFocus
                data-testid="equipment-tag-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Parent tag</Label>
              <TagTreeSelector
                tagTree={tagTree}
                tags={tags}
                selectedTagIds={parentId ? [parentId] : []}
                onChange={(ids) => setParentId(ids[0] ?? null)}
                placeholder="No parent (top level)"
                emptyLabel="No parent (top level)"
                excludedTagIds={excludedParentIds}
                singleSelect
                searchablePlaceholder="Search parent tags..."
              />
              <p className="text-xs text-muted-foreground">
                Tags can be nested up to {MAX_EQUIPMENT_TAG_DEPTH} levels, e.g.
                Audio › Mikrofon › Zubehör.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || isPending}
              data-testid="save-equipment-tag"
            >
              {tag ? "Save" : "Create Tag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
